/**
 * @note
 * The core logic is based on the body-scroll-lock library.
 *
 * @see https://github.com/willmcpo/body-scroll-lock
 */

// TODO: Replace the current Alpine scroll lock store with this version if it works well.
// Ticket: https://tubeengine.atlassian.net/browse/SB-4009

import { BodyScrollOptions } from './types';
import { lockStrategy } from './strategies';
import { isIOS } from './utils';
import { setActiveLocks, getActiveLocks, removeLock, addLock } from './active_locks';

/**
 * Disables body scrolling.
 *
 * Default: Sets `overflow: hidden` on body and documentElement
 * iOS: Sets `position: fixed` with calculated top/left positioning
 *
 * @param targetElement - The element that should remain scrollable when the scroll lock is applied
 * @param options - Additional options for the scroll lock
 */
export const disableBodyScroll = (targetElement: HTMLElement, options?: BodyScrollOptions): void => {
    if (!targetElement) {
        console.error('disableBodyScroll: targetElement is required');
        return;
    }

    addLock(targetElement, options);

    if (isIOS) {
        lockStrategy.ios.disable(targetElement);
    } else {
        lockStrategy.default.disable(options);
    }
};

/**
 * Enables body scrolling.
 *
 * Default: Restores `overflow: auto` on body and documentElement
 * iOS: Restores `position: static` and scrolls back to original position
 *
 * @param targetElement - The element that was passed to disableBodyScroll
 */
export const enableBodyScroll = (targetElement: HTMLElement): void => {
    if (!targetElement) {
        console.error('enableBodyScroll: targetElement is required');
        return;
    }

    removeLock(targetElement);

    if (isIOS) {
        lockStrategy.ios.enable(targetElement);
    } else if (getActiveLocks().length === 0) {
        lockStrategy.default.enable();
    }
};

/**
 * Clears all body scroll locks and restores original styles.
 */
export const clearAllBodyScrollLocks = (): void => {
    // Remove all touch handlers
    getActiveLocks().forEach((lock) => {
        lock.targetElement.ontouchstart = null;
        lock.targetElement.ontouchmove = null;
    });

    setActiveLocks([]);

    if (isIOS) {
        lockStrategy.ios.enable(document.body); // Pass dummy element since we're clearing all locks
    } else {
        lockStrategy.default.enable();
    }
};
